import { FC, useMemo } from "react";
import type { RecurrenceFrequency, RecurrenceInput } from "@myapp/contract";
import Select from "../../controls/Select/Select";
import { Input } from "../../controls/Input/Input";
import Checkbox from "../../controls/Checkbox/Checkbox";
import { FieldWrapper } from "../../controls/FieldWrapper/FieldWrapper";
import {
  MAX_OCCURRENCES,
  expandRecurrence,
  parseUntilDate,
} from "../../../lib/schedule/expandRecurrence";
import {
  formatDateDDMMYYYY,
  toLocalISODateOnly,
} from "../../../helpers/date";

/**
 * Recurrence rule editor + occurrence preview.
 *
 * Deliberately dumb and deliberately in `shared/`: it knows nothing about appointments or tasks,
 * which is what lets `features/appointment-upsert` and `features/task-upsert` both use it
 * without either importing the other. It renders a rule and the dates that rule produces —
 * no mutations, no queries, no series id, no `forFullSeries`.
 *
 * `startDate` is the anchor (the first occurrence), in `<input type="datetime-local">` format or
 * any parseable date string. Every later occurrence is derived from it, never from its
 * predecessor — see `shared/lib/schedule/expandRecurrence.ts`.
 */

const FREQUENCY_OPTIONS: { value: RecurrenceFrequency; label: string }[] = [
  { value: "WEEKLY", label: "Wöchentlich" },
  { value: "BIWEEKLY", label: "Alle 2 Wochen" },
  { value: "MONTHLY", label: "Monatlich" },
];

const PREVIEW_LIMIT = 12;

/**
 * Chips carry the date only. Every occurrence in a series runs at the same wall-clock time — it
 * comes from the anchor — so repeating it on each chip doubles their width to say one thing. The
 * time is stated once, next to the count.
 */
const formatTimeOfDay = (d: Date): string =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

export interface RecurrenceFieldProps {
  /** First occurrence — the anchor. Empty disables the whole control. */
  startDate: string | null | undefined;
  /** `null` means "one-time", i.e. no `recurrence` on the DTO at all. */
  value: RecurrenceInput | null;
  onChange: (value: RecurrenceInput | null) => void;
  disabled?: boolean;
  label?: string;
}

export const RecurrenceField: FC<RecurrenceFieldProps> = ({
  startDate,
  value,
  onChange,
  disabled,
  label = "Serie",
}) => {
  const anchor = useMemo(
    () => (startDate ? new Date(startDate) : null),
    [startDate],
  );
  const hasAnchor = Boolean(anchor && !Number.isNaN(anchor.getTime()));

  const preview = useMemo(() => {
    if (!hasAnchor || !value?.until) return null;
    return expandRecurrence({
      start: anchor as Date,
      frequency: value.frequency,
      until: parseUntilDate(value.until),
    });
  }, [hasAnchor, anchor, value?.frequency, value?.until]);

  const toggle = (checked: boolean) => {
    if (!checked) {
      onChange(null);
      return;
    }
    // Default horizon: one month out from the anchor, so the preview is never empty on open.
    const until = new Date((anchor as Date).getTime());
    until.setMonth(until.getMonth() + 1);
    onChange({ frequency: "WEEKLY", until: toLocalISODateOnly(until) });
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Hint sits on the checkbox row rather than below it — an off recurrence should cost one
          line, not three, since most appointments are one-offs. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Checkbox
          checked={value != null}
          onChange={hasAnchor && !disabled ? toggle : undefined}
          label={label}
        />
        {!hasAnchor && (
          <span className="text-xs text-slate-400">
            Zuerst Datum und Uhrzeit wählen
          </span>
        )}
      </div>

      {value != null && hasAnchor && (
        <>
          <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
            <FieldWrapper label="Wiederholung" className="sm:col-span-3">
              <Select
                options={FREQUENCY_OPTIONS}
                value={value.frequency}
                onChange={(next) =>
                  onChange({ ...value, frequency: next as RecurrenceFrequency })
                }
                disabled={disabled}
              />
            </FieldWrapper>

            <FieldWrapper label="Serienende" className="sm:col-span-3">
              <Input
                type="date"
                value={value.until}
                min={toLocalISODateOnly(anchor as Date)}
                onChange={(next) => onChange({ ...value, until: next })}
                disabled={disabled}
              />
            </FieldWrapper>
          </div>

          {preview?.invalidRange && (
            <p className="text-sm text-red-500">
              Das Serienende darf nicht vor dem ersten Termin liegen.
            </p>
          )}

          {preview?.overflow && (
            <p className="text-sm text-red-500">
              Zu viele Termine — maximal {MAX_OCCURRENCES} pro Serie. Bitte das
              Serienende früher setzen.
            </p>
          )}

          {/* Count and dates share one wrapping row; as a separate heading plus list they cost
              two blocks for what is a single fact. */}
          {preview && preview.occurrences.length > 0 && (
            <ul className="flex flex-wrap items-center gap-1.5">
              <li className="mr-1 text-xs font-medium text-[#124093]">
                {preview.occurrences.length} Termine, jeweils{" "}
                {formatTimeOfDay(preview.occurrences[0])}
              </li>
              {preview.occurrences.slice(0, PREVIEW_LIMIT).map((occurrence) => (
                <li
                  key={occurrence.toISOString()}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] leading-5 text-slate-600"
                >
                  {formatDateDDMMYYYY(occurrence)}
                </li>
              ))}
              {preview.occurrences.length > PREVIEW_LIMIT && (
                <li className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] leading-5 text-slate-400">
                  +{preview.occurrences.length - PREVIEW_LIMIT} weitere
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
};
